// src/pages/ELearning/LearningPathsPage.tsx — ФИЧА T
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getElearningPaths, getMyElearningPaths, deleteElearningPath } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, Plus, Trash2, Route, Users, CheckCircle, Clock, Target, ChevronRight } from 'lucide-react';

const LearningPathsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHR = ['admin', 'superadmin', 'hr_manager'].includes(user?.role || '');

  const [tab, setTab] = useState<'my' | 'all'>('my');
  const [paths, setPaths] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = isHR && tab === 'all'
        ? await getElearningPaths()
        : await getMyElearningPaths();
      setPaths(data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [tab]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить программу?')) return;
    try {
      await deleteElearningPath(id);
      setPaths(prev => prev.filter(p => (p.pathId?._id || p._id) !== id));
    } catch (e) {
      alert('Ошибка');
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .lp-hero { animation: fadeUp 0.5s ease-out; }
        .lp-card { animation: fadeUp 0.5s ease-out backwards; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .lp-card:hover { transform: translateY(-4px); box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important; }
      `}</style>

      <div className="lp-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <Route size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Программы обучения</h1>
          <p style={styles.subtitle}>Последовательные курсы для сотрудников</p>
        </div>
        {isHR && (
          <button
            onClick={() => navigate('/app/elearning/paths/new')}
            style={styles.createButton}
          >
            <Plus size={18} /> Создать программу
          </button>
        )}
      </div>

      {isHR && (
        <div style={styles.tabs}>
          <button
            onClick={() => setTab('my')}
            style={{ ...styles.tab, ...(tab === 'my' ? styles.tabActive : {}) }}
          >
            👤 Мои программы
          </button>
          <button
            onClick={() => setTab('all')}
            style={{ ...styles.tab, ...(tab === 'all' ? styles.tabActive : {}) }}
          >
            🏢 Все программы (HR)
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} className="spin" color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : paths.length === 0 ? (
        <div style={styles.empty}>
          <Route size={56} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 700 }}>
            {tab === 'all' ? 'Нет программ' : 'Вам пока не назначено программ'}
          </h3>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>
            {tab === 'all' ? 'Создайте первую программу обучения' : 'Когда HR назначит программу, она появится здесь'}
          </p>
        </div>
      ) : (
        <div style={styles.grid}>
          {paths.map((p, i) => {
            const path = p.pathId || p;
            const progress = p.pathId ? p : null;
            const totalSteps = path.steps?.length || 0;
            const currentIdx = progress?.currentStepIndex || 0;
            const completed = progress?.steps?.filter((s: any) => s.status === 'completed').length || 0;
            const progressPct = totalSteps > 0 ? Math.round((completed / totalSteps) * 100) : 0;

            return (
              <div
                key={path._id}
                className="lp-card"
                style={{ ...styles.card, animationDelay: `${i * 0.05}s` }}
                onClick={() => navigate(`/app/elearning/paths/${path._id}`)}
              >
                <div style={styles.cardStrip} />
                <div style={styles.cardHeader}>
                  <div style={styles.cardIcon}>
                    <Route size={22} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.cardTitle}>{path.name}</div>
                    {path.description && (
                      <div style={styles.cardDesc}>{path.description}</div>
                    )}
                  </div>
                  {isHR && tab === 'all' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(path._id); }}
                      style={styles.deleteBtn}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div style={styles.cardMeta}>
                  <span style={styles.metaChip}>
                    <Target size={13} /> {totalSteps} {totalSteps === 1 ? 'шаг' : (totalSteps < 5 ? 'шага' : 'шагов')}
                  </span>
                  {path.mandatory && (
                    <span style={{ ...styles.metaChip, background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
                      🔴 Обязательна
                    </span>
                  )}
                  {path.isOnboarding && (
                    <span style={{ ...styles.metaChip, background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
                      ✨ Онбординг
                    </span>
                  )}
                </div>

                {progress ? (
                  <>
                    <div style={styles.progressBar}>
                      <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
                    </div>
                    <div style={styles.progressText}>
                      <span>Пройдено {completed} / {totalSteps}</span>
                      <span style={{ fontWeight: 800, color: '#667eea' }}>{progressPct}%</span>
                    </div>
                  </>
                ) : (
                  <div style={styles.statsRow}>
                    <div style={styles.statItem}>
                      <Users size={14} /> {path.stats?.totalAssigned || 0}
                    </div>
                    <div style={styles.statItem}>
                      <CheckCircle size={14} color="#10b981" /> {path.stats?.completed || 0}
                    </div>
                    <div style={styles.statItem}>
                      <Clock size={14} color="#f59e0b" /> {path.stats?.inProgress || 0}
                    </div>
                    <ChevronRight size={18} color="#8b8fa8" style={{ marginLeft: 'auto' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(102,126,234,0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },
  createButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, boxShadow: '0 8px 24px rgba(102,126,234,0.35)' },

  tabs: { display: 'flex', gap: 6, marginBottom: 20, background: 'rgba(102,126,234,0.06)', borderRadius: 14, padding: 4 },
  tab: { flex: 1, padding: '10px 16px', border: 'none', borderRadius: 10, background: 'transparent', color: '#5a5f7a', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  tabActive: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', boxShadow: '0 6px 16px rgba(102,126,234,0.3)' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },

  card: { position: 'relative', background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(24px) saturate(180%)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 22, padding: 20, cursor: 'pointer', boxShadow: '0 8px 32px rgba(31,38,135,0.08)', overflow: 'hidden' },
  cardStrip: { position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  cardHeader: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 },
  cardIcon: { width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 6px 16px rgba(102,126,234,0.3)' },
  cardTitle: { fontSize: 16, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 },
  cardDesc: { fontSize: 12, color: '#5a5f7a', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  deleteBtn: { padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', borderRadius: 8, flexShrink: 0 },

  cardMeta: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  metaChip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'rgba(102,126,234,0.12)', color: '#667eea', borderRadius: 10, fontSize: 11, fontWeight: 700 },

  progressBar: { height: 8, background: 'rgba(102,126,234,0.1)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 },
  progressFill: { height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 4, transition: 'width 0.6s' },
  progressText: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#5a5f7a', fontWeight: 600 },

  statsRow: { display: 'flex', gap: 14, alignItems: 'center', fontSize: 13, color: '#5a5f7a', fontWeight: 600, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.05)' },
  statItem: { display: 'inline-flex', alignItems: 'center', gap: 4 },

  empty: { textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)', borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)' },
};

export default LearningPathsPage;
